import {
  BriefingPriority,
  ExecInboxThread,
  OpsTask,
  ProjectTask,
} from '../types';

/** Funnel stages treated as briefing-relevant email work. */
export const BRIEFING_URGENT_EMAIL_STAGES = new Set(['matt', 'to-respond', 'review']);

export function briefingPriorityKey(p: BriefingPriority): string {
  if (p.entityId) return `${p.source}:${p.entityId}`;
  return `${p.source}:${p.rank}:${p.href}:${p.title}`;
}

export interface BriefingPriorityResolutionContext {
  ownerUid: string;
  projectTasks: ProjectTask[];
  inboxThreads: ExecInboxThread[];
  opsTasks?: OpsTask[];
  dismissedKeys?: string[];
  /** COO briefing: hide priorities waiting on CEO mailbox owners (e.g. Matt). */
  excludeActionRequiredUids?: string[];
  /** COO briefing: hide priorities on completed/closed timber projects. */
  closedProjectIds?: Set<string>;
  now?: Date;
}

function opsTaskById(tasks: OpsTask[], taskId: string): OpsTask | undefined {
  return tasks.find((t) => t.id === taskId);
}

function taskById(tasks: ProjectTask[], taskId: string): ProjectTask | undefined {
  return tasks.find((t) => t.id === taskId);
}

function threadById(threads: ExecInboxThread[], threadId: string): ExecInboxThread | undefined {
  return threads.find((t) => t.threadId === threadId);
}

/**
 * Returns true when the underlying work appears done — priority should leave the list.
 */
export function isBriefingPriorityResolved(
  priority: BriefingPriority,
  ctx: BriefingPriorityResolutionContext,
): boolean {
  const key = briefingPriorityKey(priority);
  if (ctx.dismissedKeys?.includes(key)) return true;

  switch (priority.source) {
    case 'projectHub': {
      if (!priority.entityId) return false;
      const task = taskById(ctx.projectTasks, priority.entityId);
      if (!task) return true;
      if (task.status === 'completed') return true;
      if (task.projectId && ctx.closedProjectIds?.has(task.projectId)) return true;
      if (task.actionRequired && ctx.excludeActionRequiredUids?.includes(task.actionRequired)) {
        return true;
      }
      const rationale = priority.rationale.toLowerCase();
      const actionRequiredOnExec =
        task.actionRequired === ctx.ownerUid
        || rationale.includes('action required');
      if (actionRequiredOnExec && task.actionRequired !== ctx.ownerUid) return true;
      return false;
    }
    case 'email': {
      if (!priority.entityId) return false;
      const thread = threadById(ctx.inboxThreads, priority.entityId);
      if (!thread) return true;
      if (!BRIEFING_URGENT_EMAIL_STAGES.has(thread.stage)) return true;
      return false;
    }
    case 'calendar':
      return false;
    case 'ops': {
      if (!priority.entityId) return false;
      const task = opsTaskById(ctx.opsTasks || [], priority.entityId);
      if (!task) return true;
      if (task.status === 'completed') return true;
      if (task.actionRequired && ctx.excludeActionRequiredUids?.includes(task.actionRequired)) {
        return true;
      }
      if (task.actionRequired && task.actionRequired !== ctx.ownerUid) return true;
      return false;
    }
    case 'production':
      return false;
    default:
      return false;
  }
}

export function filterActiveBriefingPriorities(
  priorities: BriefingPriority[],
  ctx: BriefingPriorityResolutionContext,
): BriefingPriority[] {
  return priorities
    .filter((p) => !isBriefingPriorityResolved(p, ctx))
    .slice()
    .sort((a, b) => a.rank - b.rank)
    .map((p, i) => ({ ...p, rank: i + 1 }));
}
